import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog } from '@fortawesome/free-solid-svg-icons'

import '../../tailwind.css'
import '../../custom.css'
import { IBallotView, IGridSortColumn, PortalApiRoutes, IGridQueryResult, IApiResponsePayloadModel, BallotStatus, IGridFilter } from '../../types/portalTypes'
import { Grid, GridCellProps, GridColumn, GridCustomFilterCellProps, GridFilterChangeEvent, GridPageChangeEvent, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, SortDescriptor } from '@progress/kendo-data-query';
import { usePortalContext } from '../../contexts/PortalContext';
import { getBallotStatusLabel, getBallotStatusText } from '../../utils/BallotUtils';
import { toGridFilters } from '../../utils/GridUtils';
import { EnumFilterCell } from '../grid/EnumFilterCell';
import { ContainsTextFilterCell } from '../grid/ContainsTextFilterCell';
import { HeaderTdElement } from '@progress/kendo-react-data-tools';

export interface BallotsProps {
    isTest: boolean
}

export const Ballots: React.FC<BallotsProps> = ({ isTest }) => {
    const [ballots, setBallots] = useState<IBallotView[]>([])
    const [totalBallots, setTotalBallots] = useState<number>(0)
    const [pageSize, setPageSize] = useState<number>(20)
    const [pageIndex, setPageIndex] = useState<number>(0)
    const [sortColumns, setSortColumns] = useState<IGridSortColumn[]>([])
    const [isLoading, setLoading] = useState<boolean>(true)
    const [filters, setFilters] = useState<IGridFilter[]>([])

    const { legacyBaseUrl } = usePortalContext();

    const fetchBallots = useCallback(() => {
        setLoading(true)
        let ballotsApiUrl = isTest ? PortalApiRoutes.BALLOTS_TEST : PortalApiRoutes.BALLOTS
        axios.post<IApiResponsePayloadModel<IGridQueryResult<IBallotView>>>(ballotsApiUrl,
            {
                pageSize,
                pageIndex,
                sortColumns,
                filters
            })
            .then((response) => {
                setBallots(response.data.payload.results)
                setTotalBallots(response.data.payload.totalResults)
                setLoading(false)
            })
            .catch((err) => console.log(err))
    }, [pageSize, pageIndex, sortColumns, filters, isTest])

    useEffect(() => {
        fetchBallots()
    }, [fetchBallots]);

    const onPageChange = (event: GridPageChangeEvent) => {
        setPageIndex(event.page.skip / pageSize)
    }

    const onGridSort = (event: GridSortChangeEvent) => {
        console.info(event.sort)

        const sortColumns: IGridSortColumn[] = (event.sort ?? [])
            .filter((s): s is Required<Pick<SortDescriptor, 'field' | 'dir'>> => !!s.field && !!s.dir)
            .map(({ field, dir }) => ({
                member: field,
                direction: dir, // 'asc' | 'desc'
            }));

        setSortColumns(sortColumns);
    }

    const onGridFilter = (event: GridFilterChangeEvent) => {
        const filterColumns = toGridFilters(event.filter as CompositeFilterDescriptor | undefined);
        setFilters(filterColumns)
    }

    const StatusFilterCell = (props: GridCustomFilterCellProps) => (
        <HeaderTdElement columnId={props.thProps?.columnId || ''} {...props.thProps}>
            <EnumFilterCell
                {...props}
                enumType={BallotStatus}
                getText={(v) => getBallotStatusText(v)}
                defaultText="All"
            />
        </HeaderTdElement>
    );

    return (
        <>
            <Grid showLoader={isLoading}
                data={ballots}
                pageable
                onPageChange={onPageChange}
                sortable
                onSortChange={onGridSort}
                filterable
                onFilterChange={onGridFilter}
                pageSize={pageSize}
                total={totalBallots}
                scrollable="none" >
                <GridColumn width={40} filterable={false} cells={{
                    data: (props: GridCellProps) => {
                        const row = props.dataItem as IBallotView;
                        const editUrl = `${legacyBaseUrl}/AdminPortal/Ballots/Dashboard.aspx?id=${row.ballotId}`;
                        return <td><a href={editUrl} target="_blank" rel="noreferrer"><FontAwesomeIcon icon={faCog} /></a></td>;
                    }
                }} />
                <GridColumn field="ballotName" title="Name"
                    cells={{
                        filterCell: ContainsTextFilterCell
                    }}
                />
                <GridColumn field="organisation" title="Organisation" cells={{
                    data: (props: GridCellProps) => {
                        const row = props.dataItem as IBallotView;
                        const orgUrl = `${legacyBaseUrl}/AdminPortal/Orgs/OrgDetails.aspx?id=${row.organisationId}`;
                        return <td><a href={orgUrl} target="_blank" rel="noreferrer">{row.organisation}</a></td>;
                    },
                    filterCell: ContainsTextFilterCell
                }} />
                <GridColumn field="status" title="Status" width="120px" cells={{
                    data: (props: GridCellProps) => {
                        const row = props.dataItem as IBallotView;
                        return <td>{getBallotStatusLabel(row.ballotStatus)}</td>;
                    },
                    filterCell: StatusFilterCell
                }} />
                <GridColumn field="openDate" title="Open" width="250px" filterable={false} cells={{
                    data: (props: GridCellProps) => {
                        const row = props.dataItem as IBallotView;
                        return <td>{row.openDateFormattedAbbrTz ?? row.openDate}</td>;
                    },
                }} />
                <GridColumn field="closeDate" title="Close" width="250px" filterable={false} cells={{
                    data: (props: GridCellProps) => {
                        const row = props.dataItem as IBallotView;
                        return <td>{row.closeDateFormattedAbbrTz ?? row.closeDate}</td>;
                    },
                }} />
            </Grid>
        </>
    )
}